/**
 * toggle-switch-react-native
 * Toggle Switch component for react native, it works on iOS and Android
 * https://github.com/aminebenkeroum/toggle-switch-react-native
 * Email:amine.benkeroum@gmail.com
 * Blog: https://medium.com/@aminebenkeroum/
 * @benkeroumamine
 */

import React from 'react';
import {
  StyleSheet,
  View,
  TouchableOpacity,
  Animated,
  Platform,
} from 'react-native';

import PropTypes from 'prop-types';
import {colorPalette} from '../../styles/Colors';
import {isWeb} from '../../constants';

export default class ToggleSwitch extends React.Component {
  static calculateDimensions(size) {
    switch (size) {
      default:
        return {
          width: Math.min(57, (51 / 31) * 31),
          padding: 12,
          circleWidth: 27,
          circleHeight: 27,
          translateX:
            (31 / Math.min(57, (51 / 31) * 31)) * Math.min(57, (51 / 31) * 31),
        };
    }
  }

  static propTypes = {
    isOn: PropTypes.bool.isRequired,
    label: PropTypes.string,
    onColor: PropTypes.string,
    offColor: PropTypes.string,
    size: PropTypes.string,
    thumbOnStyle: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.object,
      PropTypes.number,
    ]),
    thumbOffStyle: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.object,
      PropTypes.number,
    ]),
    trackOnStyle: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.object,
      PropTypes.number,
    ]),
    trackOffStyle: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.object,
      PropTypes.number,
    ]),
    onToggle: PropTypes.func,
    disabled: PropTypes.bool,
    animationSpeed: PropTypes.number,
    useNativeDriver: PropTypes.bool,
    circleColor: PropTypes.string,
    hitSlop: PropTypes.oneOfType([
      PropTypes.shape({
        top: PropTypes.number,
        bottom: PropTypes.number,
        left: PropTypes.number,
        right: PropTypes.number,
      }),
      PropTypes.number,
    ]),
  };

  static defaultProps = {
    isOn: false,
    onColor: colorPalette.primary,
    offColor: colorPalette.grey1,
    size: 'medium',
    thumbOnStyle: {
      backgroundColor: '#fff',
      height: 27,
      width: 27,
      borderRadius: 27,
      elevation: 5,
    },
    thumbOffStyle: {
      backgroundColor: '#fff',
      height: 27,
      width: 27,
      borderRadius: 27,
      elevation: 5,
    },
    trackOnStyle: {
      height: 31,
      width: Math.min(57, (51 / 31) * 31),
    },
    trackOffStyle: {
      height: 31,
      width: Math.min(57, (51 / 31) * 31),
    },
    disabled: false,
    animationSpeed: 300,
    useNativeDriver: true,
    circleColor: 'white',
  };

  offsetX = new Animated.Value(0);
  dimensions = ToggleSwitch.calculateDimensions(this.props.size);

  createToggleSwitchStyle = () => [
    {
      justifyContent: 'center',
      width: this.dimensions.width,
      borderRadius:
        Platform.OS == 'windows' || Platform.OS == 'macos' ? 10 : 20,
      padding: this.dimensions.padding,
      backgroundColor: this.props.isOn
        ? this.props.onColor
        : this.props.offColor,
      paddingBottom:
        Platform.OS == 'windows' || Platform.OS == 'macos'
          ? this.dimensions.padding + 2
          : this.dimensions.padding,
    },
    this.props.isOn ? this.props.trackOnStyle : this.props.trackOffStyle,
  ];

  createInsideCircleStyle = () => [
    {
      alignItems: 'center',
      justifyContent: 'center',
      margin: 0,
      right: isWeb ? 4 : 5,
      position: 'absolute',
      backgroundColor: this.props.circleColor,
      transform: [{translateX: this.offsetX}],
      width: this.dimensions.circleWidth,
      height: this.dimensions.circleHeight,
      borderRadius: this.dimensions.circleWidth / 2,
      shadowColor: '#000',
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: 0.2,
      shadowRadius: 2.5,
      elevation: 1.5,
    },
    this.props.isOn ? this.props.thumbOnStyle : this.props.thumbOffStyle,
  ];

  render() {
    const {animationSpeed, useNativeDriver, isOn, onToggle, disabled, hitSlop} =
      this.props;

    let toValue;
    if (isOn) {
      toValue = -(Math.min(57, (51 / 31) * 31) - 27 - 8);
    } else {
      toValue = 0;
    }

    Animated.timing(this.offsetX, {
      toValue,
      duration: animationSpeed,
      useNativeDriver: useNativeDriver,
    }).start();

    return (
      <View style={styles.container} {...this.props}>
        <TouchableOpacity
          style={this.createToggleSwitchStyle()}
          activeOpacity={0.8}
          hitSlop={hitSlop}
          onPress={() => (disabled ? null : onToggle(!isOn))}>
          <Animated.View style={this.createInsideCircleStyle()} />
        </TouchableOpacity>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
  },
});
